import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../../Contexts/AuthContext";
import { ShippingDetails } from "../../Models/ShippingDetails";
import { OrderPaymentDetailsResponse } from "../../Models/Payment";
import { OrderNfeDetailsResponse } from "../../Models/Nfe";
import { OrderStatus } from "../../Enums/OrderStatus";
import { ShippingMethod } from "../../Enums/ShippingMethod";
import { ShippingDetailsStatus } from "../../Enums/ShippingDetailsStatus";
import { PaymentMethod } from "../../Enums/PaymentMethod";
import { NfeStatus } from "../../Enums/NfeStatus";
import { CustomerType } from "../../Enums/CustomerType";
import { PrePostingStatus } from "../../Enums/PrePostingStatus";
import { PrePostingLabelStatus } from "../../Enums/PrePostingLabelStatus";
import { PrePostingPackagingType } from "../../Enums/PrePostingPackagingType";
import { getOrderStatusLabel, getPaymentMethodLabel, getShippingMethodLabel, getNfeStatusLabel } from "../../Utils/Enums";
import { OrderSource } from "../../Enums/OrderSource";
import { formatCpf, formatCnpj, formatDateToBR, formatPriceBRL } from "../../Utils/DataFormatters";
import { FaChevronRight, FaInfoCircle } from "react-icons/fa";
import OrderNfeModal from "../OrderNfeModal/OrderNfeModal";
import OrderErrorModal from "../OrderErrorModal/OrderErrorModal";
import EmitPrePostingModal from "../EmitPrePostingModal/EmitPrePostingModal";
import Button from "../Button/Button";

const labelAutoTriggered = new Set<string>();

const useElapsedTime = (active: boolean, startTime?: string | null): string => {
  const baseRef = useRef<number | null>(null);
  const hasServerTimeRef = useRef(false);

  if (active) {
    if (baseRef.current === null) {
      baseRef.current = startTime ? new Date(startTime).getTime() : Date.now();
      hasServerTimeRef.current = !!startTime;
    } else if (!hasServerTimeRef.current && startTime) {
      // Fell back to Date.now() initially; adopt the first real server timestamp
      baseRef.current = new Date(startTime).getTime();
      hasServerTimeRef.current = true;
    }
    // Once server time is locked, ignore further startTime changes (backend polling updates)
  } else {
    baseRef.current = null;
    hasServerTimeRef.current = false;
  }

  const [elapsed, setElapsed] = useState(() => {
    if (!active || baseRef.current === null) {
      return 0;
    }
    return Math.max(0, Math.floor((Date.now() - baseRef.current) / 1000));
  });

  useEffect(() => {
    if (!active) {
      setElapsed(0);
      return;
    }
    const tick = () =>
      setElapsed(Math.max(0, Math.floor((Date.now() - (baseRef.current ?? Date.now())) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active]);

  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const LABEL_ACTIVATION_WAIT_SECONDS = 10 * 60;

const useCountdown = (totalSeconds: number, active: boolean, startTime?: string | null): { display: string; expired: boolean } => {
  const baseRef = useRef<number | null>(null);
  const hasServerTimeRef = useRef(false);

  if (active) {
    if (baseRef.current === null) {
      baseRef.current = startTime ? new Date(startTime).getTime() : Date.now();
      hasServerTimeRef.current = !!startTime;
    } else if (!hasServerTimeRef.current && startTime) {
      baseRef.current = new Date(startTime).getTime();
      hasServerTimeRef.current = true;
    }
  } else {
    baseRef.current = null;
    hasServerTimeRef.current = false;
  }

  const [remaining, setRemaining] = useState(() => {
    if (!active || baseRef.current === null) {
      return totalSeconds;
    }
    return Math.max(0, totalSeconds - Math.floor((Date.now() - baseRef.current) / 1000));
  });

  useEffect(() => {
    if (!active) {
      setRemaining(totalSeconds);
      return;
    }
    const tick = () =>
      setRemaining(Math.max(0, totalSeconds - Math.floor((Date.now() - (baseRef.current ?? Date.now())) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active, totalSeconds]);

  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  return { display: `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`, expired: remaining === 0 };
};

type OrderCardProps = {
  id: string;
  orderNumber: number;
  status: OrderStatus;
  finalPrice: number;
  shippingMethod: ShippingMethod | null;
  shippingDetails: ShippingDetails;
  createdAt: string;
  paymentMethod?: PaymentMethod;
  creditCardInstallments?: number | null;
  creditCardInterest?: number | null;
  paymentDetails?: OrderPaymentDetailsResponse;
  paymentReceivedAt?: string | null;
  nfe?: OrderNfeDetailsResponse | null;
  customerId?: string | null;
  hasOrderProducts?: boolean;
  customerName?: string;
  customerDocument?: string;
  customerType?: CustomerType;
  hasMadeToOrderItems?: boolean;
  maxMadeToOrderLeadTimeDays?: number | null;
  enablePrePostingLabel?: boolean;
  showXmlDownload?: boolean;
  onCancel: (orderId: string) => Promise<void> | void;
  onEditShippingDetailsModal?: (orderNumber: number, shippingDetails: ShippingDetails) => void;
  onTrackShippingModal?: (orderId: string, orderNumber: number) => void;
  onShowPaymentModal?: (method: PaymentMethod, paymentDetails: OrderPaymentDetailsResponse) => void;
  onEmitNfe?: () => void;
  isEmittingNfe?: boolean;
  onCancelNfe?: (justification: string) => Promise<void> | void;
  isCancellingNfe?: boolean;
  onUpdateAvailability?: (orderId: string, orderNumber: number, maxLeadTimeDays?: number | null) => void;
  onDownloadPrePostingLabel?: (orderId: string, orderNumber: number) => void;
  onRequestPrePostingLabel?: (orderId: string) => void;
  onEmitPrePosting?: (orderId: string, data: { packagingType: PrePostingPackagingType; weightGrams: number; lengthCm: number; widthCm: number; heightCm: number }) => Promise<void>;
  isEmittingPrePosting?: boolean;
  orderSource?: OrderSource;
};

const OrderCard = ({
  id,
  orderNumber,
  status,
  finalPrice,
  shippingMethod,
  shippingDetails,
  createdAt,
  paymentMethod,
  creditCardInstallments,
  creditCardInterest,
  paymentDetails,
  paymentReceivedAt,
  nfe,
  customerId,
  hasOrderProducts,
  customerName,
  customerDocument,
  customerType,
  hasMadeToOrderItems,
  maxMadeToOrderLeadTimeDays,
  enablePrePostingLabel,
  showXmlDownload = true,
  onCancel,
  onEditShippingDetailsModal,
  onTrackShippingModal,
  onShowPaymentModal,
  onEmitNfe,
  isEmittingNfe,
  onCancelNfe,
  isCancellingNfe,
  onUpdateAvailability,
  onDownloadPrePostingLabel,
  onRequestPrePostingLabel,
  onEmitPrePosting,
  isEmittingPrePosting = false,
  orderSource
}: OrderCardProps) => {
  const navigate = useNavigate();
  const { isAdmin, isOperator, user } = useAuth();

  const [showNfeModal, setShowNfeModal] = useState(false);
  const [showNfeErrorModal, setShowNfeErrorModal] = useState(false);
  const [showEmitPrePostingModal, setShowEmitPrePostingModal] = useState(false);
  const [showPrePostingErrorModal, setShowPrePostingErrorModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const basePath = isAdmin ? "/admin" : isOperator ? "/operador" : "";

  const canCompleteOrder =
    user &&
    !isAdmin &&
    !isOperator &&
    status === OrderStatus.AwaitingPayment &&
    shippingDetails.status === ShippingDetailsStatus.Saved &&
    paymentMethod == null;

  const canEditShippingDetails =
    (isAdmin || isOperator) &&
    status === OrderStatus.AwaitingShippingInfo;

  const canUpdateAvailability =
    (isAdmin || isOperator) &&
    status === OrderStatus.AwaitingAvailability &&
    Boolean(hasMadeToOrderItems);

  const canCancelOrder =
    !isOperator &&
    orderSource !== OrderSource.InStore &&
    ![OrderStatus.Cancelled, OrderStatus.Shipped, OrderStatus.Paid].includes(status);

  const isCorreiosShipping =
    shippingMethod === ShippingMethod.CorreiosPAC ||
    shippingMethod === ShippingMethod.CorreiosSedex;

  const canManagePrePosting =
    (isAdmin || isOperator) &&
    Boolean(enablePrePostingLabel) &&
    status === OrderStatus.Paid &&
    isCorreiosShipping;

  const prePostingStatus = shippingDetails.prePostingStatus;
  const labelStatus = shippingDetails.labelStatus;

  const canDownloadLabel =
    canManagePrePosting &&
    labelStatus === PrePostingLabelStatus.Ready &&
    Boolean(shippingDetails.labelReceiptId);

  const canRequestLabel =
    canManagePrePosting &&
    prePostingStatus === PrePostingStatus.Ready &&
    labelStatus === PrePostingLabelStatus.Failed;

  const canEmitPrePosting =
    canManagePrePosting &&
    (prePostingStatus == null || prePostingStatus === PrePostingStatus.Failed) &&
    Boolean(onEmitPrePosting);

  const isPrePostingPending =
    canManagePrePosting &&
    (prePostingStatus === PrePostingStatus.Pending || prePostingStatus === PrePostingStatus.Processing);

  const nfeTimer = useElapsedTime(!!isEmittingNfe);
  const prePostingTimer = useElapsedTime(!!isEmittingPrePosting);
  const isLabelProcessing =
    canManagePrePosting &&
    labelStatus === PrePostingLabelStatus.Processing;

  const prePostingPendingTimer = useElapsedTime(isPrePostingPending, shippingDetails.updatedAt);
  const labelProcessingTimer = useElapsedTime(isLabelProcessing, shippingDetails.updatedAt);

  const isLabelWaiting =
    canManagePrePosting &&
    prePostingStatus === PrePostingStatus.Ready &&
    (labelStatus === PrePostingLabelStatus.Pending || labelStatus == null);

  const { display: labelCountdown, expired: labelCountdownExpired } = useCountdown(
    LABEL_ACTIVATION_WAIT_SECONDS,
    isLabelWaiting,
    shippingDetails.updatedAt
  );

  useEffect(() => {
    if (isLabelWaiting && labelCountdownExpired && !labelAutoTriggered.has(id)) {
      labelAutoTriggered.add(id);
      onRequestPrePostingLabel?.(id);
    }
    if (!isLabelWaiting) {
      labelAutoTriggered.delete(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLabelWaiting, labelCountdownExpired]);

  useEffect(() => () => { labelAutoTriggered.delete(id); }, [id]);

  const prePostingErrorMessage = shippingDetails.prePostingLastError?.trim();

  const canTrackShipping =
    !isAdmin &&
    !isOperator &&
    isCorreiosShipping &&
    Boolean(shippingDetails.trackingCode) &&
    Boolean(onTrackShippingModal);

  const canShowPaymentModalButton =
    paymentDetails &&
    paymentMethod != null &&
    paymentMethod !== PaymentMethod.CreditCard &&
    status === OrderStatus.AwaitingPayment &&
    !paymentReceivedAt;

  const canAccessNfe =
    nfe != null &&
    (nfe.status === NfeStatus.Authorized || nfe.status === NfeStatus.Cancelled) &&
    (nfe.danfeUrl != null || nfe.xmlUrl != null);

  const canEmitNfe =
    (isAdmin || isOperator) &&
    customerId != null &&
    status === OrderStatus.Paid &&
    (!nfe || (nfe.status !== NfeStatus.Authorized && nfe.status !== NfeStatus.Cancelled)) &&
    paymentReceivedAt != null &&
    Boolean(onEmitNfe);

  const canCancelNfe =
    (isAdmin || isOperator) &&
    nfe?.status === NfeStatus.Authorized &&
    Boolean(onCancelNfe);

  const nfeStatusLabel = nfe ? getNfeStatusLabel(nfe.status) : "";
  const nfeErrorMessage = nfe?.providerMessage?.trim();
  const isNfeRetry = nfe && nfe.status !== NfeStatus.Authorized && nfe.status !== NfeStatus.Cancelled;
  const isNfeCancelled = nfe?.status === NfeStatus.Cancelled;

  const handleCancelOrder = async () => {
    setIsCancelling(true);
    try {
      await onCancel(id);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelNfe = async (justification: string) => {
    if (!onCancelNfe) {
      return;
    }
    await onCancelNfe(justification);
    setShowNfeModal(false);
  };

  const getPaymentMethodText = () => {
    if (paymentMethod == null) {
      return "";
    }

    const base = getPaymentMethodLabel(paymentMethod);

    if (paymentMethod === PaymentMethod.CreditCard && creditCardInstallments && creditCardInstallments > 1) {
      const hasInterest = creditCardInterest != null && creditCardInterest > 0;
      return `${base} (${creditCardInstallments}x ${hasInterest ? "com juros" : "sem juros"})`;
    }

    return base;
  };

  const formattedCustomerDocument = (() => {
    if (!customerDocument) {
      return "";
    }
    if (customerType === CustomerType.IndividualPerson) {
      return formatCpf(customerDocument);
    }
    if (customerType === CustomerType.LegalEntity) {
      return formatCnpj(customerDocument);
    }
  })();

  const customerDocumentLabel =
    customerType === CustomerType.LegalEntity
      ? "CNPJ: "
      : customerType === CustomerType.IndividualPerson
      ? "CPF: "
      : "";

  return (
    <>
    <div className="bg-white flex flex-col border border-gray-300 rounded mb-5">
      <div className="flex flex-col sm:flex-row items-center justify-between border-b border-gray-300 p-4 gap-2 bg-primary-120">
        <div className="flex items-center justify-center sm:justify-start">
          <h3 className="text-md font-semibold">{"Pedido"} #{orderNumber}</h3>
        </div>
        <div className="w-full sm:w-auto flex flex-col sm:flex-row items-center gap-2">
          {canShowPaymentModalButton && (
            <Button
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto"
              type="button"
              onClick={() => onShowPaymentModal?.(paymentMethod!, paymentDetails!)}
            >
              {"REALIZAR PAGAMENTO"}
            </Button>
          )}

          {canTrackShipping && (
            <Button
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto"
              type="button"
              onClick={() => onTrackShippingModal?.(id, orderNumber)}
            >
              {"RASTREAR PEDIDO"}
            </Button>
          )}

          {canAccessNfe && (
            <Button
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto"
              onClick={() => setShowNfeModal(true)}
            >
              {"NOTA FISCAL"}
            </Button>
          )}

          {isNfeCancelled && (isAdmin || isOperator) && (
            <div className="w-full sm:flex-1 bg-gray-100 border border-gray-200 text-gray-800 text-sm font-bold rounded p-2">
              {"Status da NF-e"}: {nfeStatusLabel}
            </div>
          )}

          {isNfeRetry && !canEmitNfe && (isAdmin || isOperator) && (
            <button
              type="button"
              onClick={() => setShowNfeErrorModal(true)}
              className="w-full sm:flex-1 bg-gray-100 border border-gray-200 text-gray-800 text-sm font-bold rounded p-2 text-left hover:border-gray-300"
            >
              <span className="flex items-center justify-between gap-2">
                <span className="whitespace-nowrap">{"Status da NF-e"}: {nfeStatusLabel}</span>
                <span className="text-gray-500 shrink-0"><FaInfoCircle /></span>
              </span>
            </button>
          )}

          {canEmitNfe && (
            <Button
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto"
              onClick={isNfeRetry ? () => setShowNfeErrorModal(true) : onEmitNfe}
              disabled={isEmittingNfe}
            >
              {isEmittingNfe ? (
                <><span>{"EMITINDO NOTA FISCAL..."}</span><span className="inline-block min-w-[2.5rem] text-right tabular-nums">{nfeTimer}</span></>
              ) : isNfeRetry ? (
                <><FaInfoCircle className="shrink-0" />{"RE-EMITIR NOTA FISCAL"}</>
              ) : (
                "EMITIR NOTA FISCAL"
              )}
            </Button>
          )}

          {canDownloadLabel && (
            <Button
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto"
              onClick={() => onDownloadPrePostingLabel?.(id, orderNumber)}
            >
              {"BAIXAR RÓTULO CORREIOS"}
            </Button>
          )}

          {(isPrePostingPending || isLabelProcessing) && (
            <Button
              disabled
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto opacity-60 cursor-not-allowed"
            >
              <span>{isLabelProcessing ? "EMITINDO RÓTULO CORREIOS..." : "CRIANDO PRÉ-POSTAGEM..."}</span><span className="inline-block min-w-[2.5rem] text-right tabular-nums">{isLabelProcessing ? labelProcessingTimer : prePostingPendingTimer}</span>
            </Button>
          )}

          {canEmitPrePosting && (
            <Button
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto"
              onClick={prePostingStatus === PrePostingStatus.Failed ? () => setShowPrePostingErrorModal(true) : () => setShowEmitPrePostingModal(true)}
              disabled={isEmittingPrePosting}
            >
              {isEmittingPrePosting ? (
                <><span>{"CRIANDO PRÉ-POSTAGEM..."}</span><span className="inline-block min-w-[2.5rem] text-right tabular-nums">{prePostingTimer}</span></>
              ) : prePostingStatus === PrePostingStatus.Failed ? (
                <><FaInfoCircle className="shrink-0" />{"RE-CRIAR PRÉ-POSTAGEM"}</>
              ) : (
                "CRIAR PRÉ-POSTAGEM"
              )}
            </Button>
          )}

          {isLabelWaiting && !labelCountdownExpired && (
            <Button
              disabled
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto opacity-60 cursor-not-allowed"
            >
              <span>{"AGUARDANDO ATIVAÇÃO CORREIOS..."}</span>
              <span className="inline-block min-w-[2.5rem] text-right tabular-nums">{labelCountdown}</span>
            </Button>
          )}

          {isLabelWaiting && labelCountdownExpired && (
            <Button
              disabled
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto opacity-60 cursor-not-allowed"
            >
              {"AGUARDANDO PROCESSAMENTO CORREIOS..."}
            </Button>
          )}

          {canRequestLabel && (
            <Button
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto"
              onClick={() => onRequestPrePostingLabel?.(id)}
            >
              {"EMITIR RÓTULO CORREIOS"}
            </Button>
          )}

          {canCompleteOrder && (
              <Button
                className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto"
                onClick={() => navigate(`/pedidos/concluir/${id}`)}
              >
                {"CONCLUIR PEDIDO"}
              </Button>
          )}

          {canEditShippingDetails && (
              <Button
                className="text-center text-sm p-2 w-full sm:w-auto"
                onClick={() => onEditShippingDetailsModal?.(orderNumber, shippingDetails)}
              >
                {"ATUALIZAR ENVIO"}
              </Button>
          )}

          {canUpdateAvailability && (
            <Button
              className="text-center text-sm p-2 w-full sm:w-auto"
              onClick={() => onUpdateAvailability?.(id, orderNumber, maxMadeToOrderLeadTimeDays)}
            >
              {"ATUALIZAR DISPONIBILIDADE"}
            </Button>
          )}

          {canCancelOrder && (
            <Button isLoading={isCancelling}
              className="text-sm p-2 w-full sm:w-auto"
              onClick={handleCancelOrder}
              disabled={isCancelling}
            >
              {"CANCELAR PEDIDO"}
            </Button>
          )}
                 
          {(orderSource !== OrderSource.InStore || customerId != null || hasOrderProducts) && (
            <Button variant="secondary"
              className="flex justify-center items-center gap-1 text-sm p-2 w-full sm:w-auto"
              onClick={() => navigate(`${basePath}/pedidos/${id}`)}
            >
              {"VER DETALHES"}
              <FaChevronRight />
            </Button>
          )}
        </div>
      </div>

<div className="text-sm px-4 py-2 border-b border-gray-300">
        <p><strong>{"Data do pedido"}: </strong>{formatDateToBR(createdAt)}</p>
      </div>

      {(isAdmin || isOperator) && customerId != null && customerName && (
        <div className="text-sm px-4 py-2 border-b border-gray-300 flex flex-col md:flex-row md:flex-wrap gap-x-2 gap-y-1">
          <span className="truncate md:overflow-visible md:whitespace-normal" title={customerName}><strong>{"Cliente"}:</strong> {customerName}</span>
          {formattedCustomerDocument && (
            <>
              <span aria-hidden="true" className="hidden md:inline">•</span>
              <span>
                <strong>{customerDocumentLabel}</strong>{formattedCustomerDocument}
              </span>
            </>
          )}
        </div>
      )}

      {orderSource !== OrderSource.InStore && (
        <div className="text-sm px-4 py-2 border-b border-gray-300">
          <p><strong>{"Status"}: </strong>{getOrderStatusLabel(status)}</p>
        </div>
      )}

      {hasMadeToOrderItems && (
        <div className="text-sm px-4 py-2 border-b border-gray-300 bg-orange-50">
          <p className="font-semibold text-orange-700">{"Itens sob encomenda"}</p>
          <p className="text-orange-700">
            {maxMadeToOrderLeadTimeDays != null
              ? `${"Prazo em até"} ${maxMadeToOrderLeadTimeDays} ${maxMadeToOrderLeadTimeDays === 1 ? "dia" : "dias"}.`
              : "Aguardando definição de disponibilidade."}
          </p>
        </div>
      )}

      {orderSource !== OrderSource.InStore && (
        <div className="text-sm px-4 py-2 border-b border-gray-300">
          <p className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-x-2 gap-y-1">
            <span>
              <strong>{"Método de entrega"}:</strong> {getShippingMethodLabel(shippingMethod)}
            </span>
            {shippingDetails.trackingCode && isCorreiosShipping && (
              <>
                <span aria-hidden="true" className="hidden md:inline">•</span>
                <span>
                  <strong>{"Código de rastreio"}:</strong> {shippingDetails.trackingCode}
                </span>
              </>
            )}
          </p>
        </div>
      )}

      {paymentMethod != null && (
        <div className="text-sm px-4 py-2 border-b border-gray-300">
          <p className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-x-2 gap-y-1">
            <span>
              <strong>{"Pagamento via"}:</strong> {getPaymentMethodText()}
            </span>
            {paymentReceivedAt && orderSource !== OrderSource.InStore && (
              <>
                <span aria-hidden="true" className="hidden md:inline">•</span>
                <span>
                  <strong>{"Realizado em"}:</strong> {formatDateToBR(paymentReceivedAt)}
                </span>
              </>
            )}
          </p>
        </div>
      )}

      <div className="text-sm px-4 py-2">
        <p><strong>{"Total"}: </strong>{formatPriceBRL(finalPrice)}</p>
      </div>
    </div>

    <OrderNfeModal
      isOpen={showNfeModal}
      orderNumber={orderNumber}
      danfeUrl={nfe?.danfeUrl ?? null}
      xmlUrl={nfe?.xmlUrl ?? null}
      showXmlDownload={showXmlDownload}
      cancellationPdfUrl={nfe?.cancellationPdfUrl ?? null}
      cancellationXmlUrl={nfe?.cancellationXmlUrl ?? null}
      isCancelled={isNfeCancelled}
      canCancel={canCancelNfe}
      isCancelling={isCancellingNfe}
      onCancel={handleCancelNfe}
      onClose={() => setShowNfeModal(false)}
    />

    <OrderErrorModal
      isOpen={showNfeErrorModal}
      onClose={() => setShowNfeErrorModal(false)}
      title={"Detalhes do erro da NF-e"}
      statusLabel={nfeStatusLabel}
      message={nfeErrorMessage}
      onRetry={canEmitNfe ? () => { setShowNfeErrorModal(false); onEmitNfe?.(); } : undefined}
      retryLabel={canEmitNfe ? "RE-EMITIR NOTA FISCAL" : undefined}
    />

    <EmitPrePostingModal
      isOpen={showEmitPrePostingModal}
      orderNumber={orderNumber}
      onClose={() => setShowEmitPrePostingModal(false)}
      onSubmit={(data) => onEmitPrePosting?.(id, data)}
    />

    <OrderErrorModal
      isOpen={showPrePostingErrorModal}
      onClose={() => setShowPrePostingErrorModal(false)}
      title={"Detalhes do erro da Postagem"}
      statusLabel={"Falhou"}
      message={prePostingErrorMessage}
      onRetry={() => { setShowPrePostingErrorModal(false); setShowEmitPrePostingModal(true); }}
      retryLabel={"RE-CRIAR PRÉ-POSTAGEM"}
    />
    </>
  );
};

export default OrderCard;
